(function () {
  'use strict';

  /*
    SFG Support UI
    ==============

    Generic renderer/state engine for the declarative rules in
    window.SFGSupportFlow.

    Dependencies (load before this asset):
      window.SFGVehicleDecoder
      window.SFGEngineDTCData
      window.SFGEFILiveErrorData
      window.SFGSupportCodes
      window.SFGSupportFlow

    This asset intentionally does not use localStorage/sessionStorage.
    Shopify's native contact-form submission path is preserved so the
    platform CAPTCHA can run normally.
  */

  var SUPPORT_MARKER = 'SFG_SUPPORT_TICKET_V1';
  var SUPPORT_EMAIL = 'Support@SpeedFarmGarage.com';

  function text(value) {
    return String(value == null ? '' : value).trim();
  }

  function unique(list) {
    var seen = Object.create(null);
    return (list || []).filter(function (value) {
      value = text(value);
      if (!value || seen[value]) return false;
      seen[value] = true;
      return true;
    });
  }

  function cloneArray(list) {
    return (list || []).slice();
  }

  function cloneContext(ctx) {
    var out = {};
    Object.keys(ctx || {}).forEach(function (key) {
      out[key] = ctx[key];
    });
    return out;
  }

  function E(tag, className, value) {
    var el = document.createElement(tag);
    if (className) el.className = className;
    if (value !== undefined) el.textContent = value;
    return el;
  }

  function button(label, className) {
    var el = E('button', className || 'sfg-btn', label);
    el.type = 'button';
    return el;
  }

  function linkButton(label, url, className) {
    var el = E('a', className || 'sfg-btn sfg-btn-alt', label);
    el.href = url;
    el.target = '_blank';
    el.rel = 'noopener';
    return el;
  }

  function addParagraph(parent, className, value) {
    if (!value) return null;
    var p = E('p', className || '', value);
    parent.appendChild(p);
    return p;
  }

  function initSupport(root) {
    if (!root || root.dataset.sfgSupportInitialized === '1') return;

    var Flow = window.SFGSupportFlow;
    var Codes = window.SFGSupportCodes;

    if (!Flow) {
      console.warn('SFG Support UI: sfg-support-flow.js is not loaded.');
      return;
    }

    root.dataset.sfgSupportInitialized = '1';

    var content = root.querySelector('.sfg-content');
    var nav = root.querySelector('.sfg-nav');
    var back = root.querySelector('.sfg-back');
    var progress = root.querySelector('.sfg-progress');
    var progressBar = progress && progress.querySelector('span');
    var progressText = progress && progress.querySelector('small');
    var ticket = root.querySelector('.sfg-ticket');
    var ticketFormId = root.dataset.ticketFormId || '';
    var ticketForm = ticketFormId ? document.getElementById(ticketFormId) : root.querySelector('.sfg-ticket-form');
    var submitButton = root.querySelector('[data-submit]');
    var ticketBackButton = root.querySelector('[data-ticket-back]');
    var validationBox = root.querySelector('[data-validation]');
    var rootAnchor = root.id || 'sfg-support';

    if (!content || !nav || !back || !progress || !progressBar || !progressText || !ticket || !ticketForm) {
      console.warn('SFG Support UI: required Support markup is missing.');
      return;
    }

    var state = freshState();
    var navHistory = [];
    var pending = null;
    var view = 'categories';
    var busy = false;

    function freshState() {
      return {
        flowKey: '',
        nodeId: '',
        context: {},
        answers: [],
        unresolved: [],
        resultNotes: [],
        flags: [],
        evidence: [],
        resultLink: '',
        resultLinkLabel: '',
        status: Flow.status.READY,
        follow: '',
        decodedVin: '',
        vinDecodeTrusted: null,
        vehicle: null,
        decoded: null,
        dtcAnalysis: null,
        efiAnalysis: null,
        sequence: null,
        ticketId: null
      };
    }

    function snapshot() {
      return {
        flowKey: state.flowKey,
        nodeId: state.nodeId,
        context: cloneContext(state.context),
        answers: state.answers.slice(),
        unresolved: cloneArray(state.unresolved),
        resultNotes: cloneArray(state.resultNotes),
        flags: cloneArray(state.flags),
        evidence: (state.evidence || []).map(function (item) { return cloneContext(item); }),
        resultLink: state.resultLink,
        resultLinkLabel: state.resultLinkLabel,
        status: state.status,
        follow: state.follow,
        decodedVin: state.decodedVin,
        vinDecodeTrusted: state.vinDecodeTrusted,
        vehicle: state.vehicle,
        decoded: state.decoded,
        dtcAnalysis: state.dtcAnalysis,
        efiAnalysis: state.efiAnalysis,
        sequence: state.sequence ? {
          queue: cloneArray(state.sequence.queue),
          next: state.sequence.next,
          sourceNode: state.sequence.sourceNode,
          kind: state.sequence.kind
        } : null,
        ticketId: state.ticketId
      };
    }

    function restore(saved) {
      state.flowKey = saved.flowKey;
      state.nodeId = saved.nodeId;
      state.context = cloneContext(saved.context);
      state.answers = saved.answers.slice();
      state.unresolved = cloneArray(saved.unresolved);
      state.resultNotes = cloneArray(saved.resultNotes);
      state.flags = cloneArray(saved.flags || []);
      state.evidence = (saved.evidence || []).map(function (item) { return cloneContext(item); });
      state.resultLink = saved.resultLink;
      state.resultLinkLabel = saved.resultLinkLabel;
      state.status = saved.status;
      state.follow = saved.follow;
      state.decodedVin = saved.decodedVin;
      state.vinDecodeTrusted = saved.vinDecodeTrusted;
      state.vehicle = saved.vehicle;
      state.decoded = saved.decoded;
      state.dtcAnalysis = saved.dtcAnalysis;
      state.efiAnalysis = saved.efiAnalysis;
      state.sequence = saved.sequence ? {
        queue: cloneArray(saved.sequence.queue),
        next: saved.sequence.next,
        sourceNode: saved.sequence.sourceNode,
        kind: saved.sequence.kind
      } : null;
      state.ticketId = saved.ticketId;
    }

    function pushHistory() {
      navHistory.push(snapshot());
    }

    function resetState() {
      state = freshState();
      navHistory = [];
      pending = null;
      busy = false;
      view = 'categories';
    }

    function currentFlow() {
      return Flow.getFlow(state.flowKey);
    }

    function addAnswer(question, answer) {
      state.answers.push({
        question: text(question),
        answer: text(answer)
      });
    }

    function addUnresolved(value) {
      value = text(value);
      if (value && state.unresolved.indexOf(value) < 0) state.unresolved.push(value);
    }

    function addResultNote(value) {
      value = text(value);
      if (value && state.resultNotes.indexOf(value) < 0) state.resultNotes.push(value);
    }

    function addFlag(value) {
      value = text(value);
      if (value && state.flags.indexOf(value) < 0) state.flags.push(value);
    }

    function addEvidence(item) {
      if (!item) return;
      var list = Array.isArray(item) ? item : [item];
      list.forEach(function (entry) {
        if (!entry || !entry.type) return;
        var normalized = {
          type: text(entry.type),
          weight: Number(entry.weight || 0),
          reason: text(entry.reason || '')
        };
        var duplicate = state.evidence.some(function (existing) {
          return existing.type === normalized.type && existing.weight === normalized.weight && existing.reason === normalized.reason;
        });
        if (!duplicate) state.evidence.push(normalized);
      });
    }

    function finalizeStatus() {
      if (Flow && typeof Flow.resolveStatus === 'function') {
        state.status = Flow.resolveStatus(state.evidence || [], state.status);
      }
      return state.status;
    }

    function clearVinState() {
      state.decodedVin = '';
      state.vinDecodeTrusted = null;
      state.vehicle = null;
      state.decoded = null;
      state.context.vehicle = null;
      delete state.context.supportedApplication;
      delete state.context.controlMethod;
      delete state.context.fpcmGaugeIssue;
    }

    function applyMeta(meta) {
      if (!meta) return;

      if (meta.status) {
        state.status = meta.status;
        if (meta.status === Flow.status.VEHICLE && !meta.evidence) addEvidence({ type: 'vehicle', weight: 4, reason: meta.note || 'Vehicle-side evidence from troubleshooting response.' });
        if (Flow.status.CALIBRATION && meta.status === Flow.status.CALIBRATION && !meta.evidence) addEvidence({ type: 'calibration', weight: 4, reason: meta.note || 'Calibration-side evidence from troubleshooting response.' });
        if (Flow.status.CONFIGURATION && meta.status === Flow.status.CONFIGURATION && !meta.evidence) addEvidence({ type: 'configuration', weight: 4, reason: meta.note || 'Calibration-configuration evidence from troubleshooting response.' });
        if (meta.status === Flow.status.REVISION && !meta.evidence) addEvidence({ type: 'revision', weight: 5, reason: meta.note || 'Vehicle hardware/configuration differs from the tune order.' });
      }
      if (meta.evidence) addEvidence(meta.evidence);
      if (meta.flag) addFlag(meta.flag);
      if (meta.flags) (meta.flags || []).forEach(addFlag);
      if (meta.follow) state.follow = meta.follow;
      if (meta.note) addResultNote(meta.note);
      if (meta.resultLink) state.resultLink = meta.resultLink;
      if (meta.resultLinkLabel) state.resultLinkLabel = meta.resultLinkLabel;

      if (meta.platform !== undefined) state.context.platform = meta.platform;
      if (meta.concern !== undefined) state.context.concern = meta.concern;
      if (meta.operation !== undefined) state.context.operation = meta.operation;
      if (meta.controlMethod !== undefined) state.context.controlMethod = meta.controlMethod;
      if (meta.nonVgt !== undefined) state.context.nonVgt = !!meta.nonVgt;
      if (meta.gridHeaterAltered !== undefined) state.context.gridHeaterAltered = !!meta.gridHeaterAltered;
      if (meta.ccvAltered !== undefined) state.context.ccvAltered = !!meta.ccvAltered;
      if (meta.liftPumpAltered !== undefined) state.context.liftPumpAltered = !!meta.liftPumpAltered;
      if (meta.fpcmGaugeIssue !== undefined) state.context.fpcmGaugeIssue = !!meta.fpcmGaugeIssue;
      if (meta.throttleAltered !== undefined) state.context.throttleAltered = !!meta.throttleAltered;
      if (meta.egrAltered !== undefined) state.context.egrAltered = !!meta.egrAltered;
      if (meta.exhaustSensorAltered !== undefined) state.context.exhaustSensorAltered = !!meta.exhaustSensorAltered;
      if (meta.dpfAltered !== undefined) state.context.dpfAltered = !!meta.dpfAltered;
      if (meta.scrModuleAltered !== undefined) state.context.scrModuleAltered = !!meta.scrModuleAltered;
      if (meta.writeFailedAfterErase !== undefined) state.context.writeFailedAfterErase = !!meta.writeFailedAfterErase;

      if (meta.followByPlatform) {
        state.follow = Flow.platformFollow(state.context.platform || '');
      }
    }

    function statusHeadline() {
      if (state.status === Flow.status.RECOVERY) return 'Programming recovery required';
      if (Flow.status.RETRY_FLASH && state.status === Flow.status.RETRY_FLASH) return 'Retry the full flash without removing controller power';
      if (state.status === Flow.status.VEHICLE) return 'Vehicle-side issue is more likely';
      if (Flow.status.CALIBRATION && state.status === Flow.status.CALIBRATION) return 'Calibration issue is possible';
      if (Flow.status.CONFIGURATION && state.status === Flow.status.CONFIGURATION) return 'Calibration configuration needs review';
      if (state.status === Flow.status.REVISION) return 'Tune revision may be required';
      if (state.status === Flow.status.CONTROLLER) return 'Controller/file update required';
      if (state.status === Flow.status.GENERAL) return 'Ready for SFG Support';
      return 'Ready for SFG Support';
    }

    function statusDescription() {
      if (state.status === Flow.status.RECOVERY) {
        return 'Stop experimenting with unrelated files or procedures. Create a recovery ticket so SFG Support can review the exact programming state.';
      }
      if (Flow.status.RETRY_FLASH && state.status === Flow.status.RETRY_FLASH) {
        return 'Keep the controller powered and retry the full flash procedure. Do not remove controller power while EFILive’s boot loader is waiting for a successful full flash. If the full flash still will not complete, create a ticket without removing power.';
      }
      if (state.status === Flow.status.VEHICLE) {
        return 'The current information points more strongly toward a vehicle-side issue than a calibration issue. You can still create a ticket so SFG Support can review it.';
      }
      if (Flow.status.CALIBRATION && state.status === Flow.status.CALIBRATION) {
        return 'The collected evidence makes the calibration a meaningful possibility. Create a ticket so SFG can review the file and supporting information.';
      }
      if (Flow.status.CONFIGURATION && state.status === Flow.status.CONFIGURATION) {
        return 'The vehicle setup and the calibration configuration may not match. SFG should verify what the tune was intended to account for before the issue is treated as a vehicle fault.';
      }
      if (state.status === Flow.status.REVISION) {
        return 'The current hardware or controller configuration may require updated tuning. A paid tune revision may be required when the hardware differs from the configuration originally ordered; SFG can confirm before you order anything.';
      }
      if (state.status === Flow.status.CONTROLLER) {
        return 'The controller/file combination needs SFG review before additional programming is attempted.';
      }
      return 'Create a ticket if the concern remains after the relevant checks above.';
    }

    function progressStep(label) {
      progress.hidden = false;
      var step = Math.max(1, state.answers.length + 1);
      progressBar.style.width = Math.min(12 + step * 8, 90) + '%';
      progressText.textContent = label || ('Relevant check ' + step);
    }

    function hideTicket() {
      ticket.hidden = true;
    }

    function showQuestionShell(node, progressLabel) {
      hideTicket();
      view = 'question';
      pending = null;
      content.innerHTML = '';
      nav.hidden = false;
      back.textContent = navHistory.length ? 'Back' : 'Change problem type';
      progressStep(progressLabel);
      content.appendChild(E('h3', 'sfg-q', node.label || 'Support check'));
      if (node.description) content.appendChild(E('p', 'sfg-desc', node.description));
    }

    function begin(flowKey) {
      var flow = Flow.getFlow(flowKey);
      if (!flow) return;

      resetState();
      state.flowKey = flowKey;
      state.status = flow.status || Flow.status.READY;
      resetTicketForm();

      if (flow.direct) {
        ticketScreen();
        return;
      }

      state.nodeId = flow.start;
      renderCurrent();
    }

    function categories() {
      resetState();
      resetTicketForm();
      hideTicket();
      view = 'categories';
      nav.hidden = true;
      progress.hidden = true;
      content.innerHTML = '';

      content.appendChild(E('h3', 'sfg-q', 'Choose the closest type of problem'));
      content.appendChild(E('p', 'sfg-desc', 'Use the guided checks for common issues, or choose General Support / Other to send a ticket without troubleshooting.'));

      var grid = E('div', 'sfg-grid');
      Flow.categories().forEach(function (item) {
        var b = button('', 'sfg-card');
        b.appendChild(E('strong', '', item.title));
        b.appendChild(E('small', '', item.description));
        b.addEventListener('click', function () {
          begin(item.key);
        });
        grid.appendChild(b);
      });
      content.appendChild(grid);
    }

    function renderCurrent() {
      if (!state.nodeId || state.nodeId === 'RESULT') {
        resultsScreen();
        return;
      }

      if (state.sequence && state.sequence.queue.length) {
        renderCheck(state.sequence.queue[0]);
        return;
      }

      var node = Flow.getNode(state.nodeId);
      if (!node) {
        console.warn('SFG Support UI: unknown flow node:', state.nodeId);
        resultsScreen();
        return;
      }

      applyMeta(node.meta);

      if (node.type === 'router') {
        state.nodeId = Flow.routeNode(node, state.context) || 'RESULT';
        renderCurrent();
        return;
      }

      if (node.type === 'check_sequence') {
        startSequence(node.checks || [], node.next || 'RESULT', node.id, 'static');
        return;
      }

      if (node.type === 'dynamic_vehicle_checks') {
        startDynamicVehicleChecks(node);
        return;
      }

      if (node.type === 'dynamic_efi_checks') {
        startDynamicEfiChecks(node);
        return;
      }

      if (node.type === 'resolved') {
        resolvedScreen(node);
        return;
      }

      if (node.type === 'vin') {
        renderVin(node);
        return;
      }

      if (node.type === 'dtc') {
        renderDtc(node);
        return;
      }

      if (node.type === 'efilive_error') {
        renderEfiError(node);
        return;
      }

      if (node.type === 'text') {
        renderTextNode(node);
        return;
      }

      if (node.type === 'choice') {
        renderChoiceNode(node);
        return;
      }

      console.warn('SFG Support UI: unsupported node type:', node.type);
      resultsScreen();
    }

    function advanceNode(next) {
      state.nodeId = next || 'RESULT';
      state.sequence = null;
      renderCurrent();
    }

    function renderTextNode(node) {
      showQuestionShell(node);

      var input = E('textarea', 'sfg-text');
      input.placeholder = node.placeholder || '';
      content.appendChild(input);

      var actions = E('div', 'sfg-actions');
      var nextButton = button('Continue');
      nextButton.addEventListener('click', function () {
        var value = text(input.value);
        if (!value) {
          input.focus();
          return;
        }

        pushHistory();
        addAnswer(node.label, value);
        advanceNode(node.next || 'RESULT');
      });
      actions.appendChild(nextButton);
      content.appendChild(actions);
      input.focus();
    }

    function renderChoiceNode(node) {
      showQuestionShell(node);
      var grid = E('div', 'sfg-grid');

      (node.options || []).forEach(function (option) {
        var b = button(option.label, 'sfg-choice');
        b.addEventListener('click', function () {
          selectChoice(node, option, false);
        });
        grid.appendChild(b);
      });

      content.appendChild(grid);
    }

    function selectChoice(node, option, isCheck) {
      if (option.infoBack) {
        pending = { node: node, option: option, isCheck: isCheck };
        infoBackScreen(option.infoBack);
        return;
      }

      if (option.stop) {
        pending = { node: node, option: option, isCheck: isCheck };
        stopScreen(option.stop);
        return;
      }

      if (option.warning) {
        pending = { node: node, option: option, isCheck: isCheck };
        warningScreen(option.warning);
        return;
      }

      commitChoice(node, option, isCheck, false, '');
    }

    function commitChoice(node, option, isCheck, warned, unresolvedKey) {
      pushHistory();

      if (unresolvedKey) addUnresolved(unresolvedKey);
      applyMeta(option.meta);

      addAnswer(
        node.label,
        option.label + (warned ? ' — continued despite warning' : '')
      );

      if (isCheck) {
        completeCurrentCheck(option.addChecks || [], option.removeChecks || []);
      } else {
        advanceNode(option.next || node.next || 'RESULT');
      }
    }

    function resolvedScreen(node) {
      view = 'resolved';
      pending = null;
      hideTicket();
      nav.hidden = true;
      progress.hidden = false;
      progressBar.style.width = '100%';
      progressText.textContent = 'Problem resolved';
      content.innerHTML = '';

      content.appendChild(E('h3', 'sfg-q', node.title || 'Problem resolved'));

      var box = E('div', 'sfg-result');
      box.appendChild(E('h4', '', 'No support ticket needed'));
      box.appendChild(E('p', '', node.text || 'The issue is resolved. No support ticket is needed.'));
      content.appendChild(box);

      var actions = E('div', 'sfg-actions');
      var restart = button('Back to Support Menu', 'sfg-btn sfg-btn-alt');
      restart.addEventListener('click', categories);
      actions.appendChild(restart);
      content.appendChild(actions);
    }

    function infoBackScreen(info) {
      view = 'info';
      nav.hidden = true;
      progress.hidden = false;
      content.innerHTML = '';

      content.appendChild(E('h3', 'sfg-q', info.title || 'Check this first'));

      var box = E('div', 'sfg-result');
      box.appendChild(E('p', '', info.text || 'Check this item, then return to the previous question.'));
      content.appendChild(box);

      var actions = E('div', 'sfg-actions');
      var b = button(info.button || 'Back', 'sfg-btn sfg-btn-alt');
      b.addEventListener('click', function () {
        pending = null;
        renderCurrent();
      });
      actions.appendChild(b);
      content.appendChild(actions);
    }

    function stopScreen(stop) {
      view = 'stop';
      nav.hidden = true;
      progress.hidden = false;
      content.innerHTML = '';
      content.appendChild(E('h3', 'sfg-q', 'A required issue must be corrected first'));

      var box = E('div', 'sfg-stop');
      box.appendChild(E('h4', '', stop.title || 'Correct this issue first'));
      box.appendChild(E('p', '', stop.text || 'Correct this issue before continuing.'));
      content.appendChild(box);

      var actions = E('div', 'sfg-actions');
      if (stop.url) actions.appendChild(linkButton(stop.button || 'Open Link', stop.url));

      var ticketAnyway = button('Create Support Ticket Anyway');
      ticketAnyway.addEventListener('click', function () {
        var p = pending;
        pending = null;

        if (p) {
          pushHistory();
          applyMeta(p.option && p.option.meta);
          addAnswer(
            p.node && p.node.label || 'Required check',
            (p.option && p.option.label || 'Not completed') + ' — ticket created before required correction'
          );
        }

        addUnresolved(stop.unresolved || ('Required check not completed: ' + (stop.title || 'Correct this issue first')));
        ticketScreen();
      });
      actions.appendChild(ticketAnyway);

      var b = button('Back to This Check', 'sfg-btn sfg-btn-alt');
      b.addEventListener('click', function () {
        pending = null;
        renderCurrent();
      });
      actions.appendChild(b);

      var restart = button('Start Over', 'sfg-btn sfg-btn-alt');
      restart.addEventListener('click', categories);
      actions.appendChild(restart);
      content.appendChild(actions);
    }

    function warningScreen(warning) {
      view = 'warning';
      nav.hidden = true;
      progress.hidden = false;
      content.innerHTML = '';
      content.appendChild(E('h3', 'sfg-q', 'Important check'));

      var box = E('div', 'sfg-warning');
      box.appendChild(E('h4', '', warning.title || 'Important check'));
      box.appendChild(E('p', '', warning.text || 'This check is incomplete.'));
      content.appendChild(box);

      var actions = E('div', 'sfg-actions');
      if (warning.url) actions.appendChild(linkButton(warning.button || 'Open Instructions', warning.url, 'sfg-btn sfg-btn-alt'));

      var continueButton = button('Continue Anyway');
      continueButton.addEventListener('click', function () {
        var p = pending;
        pending = null;
        if (!p) return renderCurrent();
        commitChoice(p.node, p.option, p.isCheck, true, warning.unresolved || '');
      });

      var backButton = button('Back', 'sfg-btn sfg-btn-alt');
      backButton.addEventListener('click', function () {
        pending = null;
        renderCurrent();
      });

      actions.appendChild(continueButton);
      actions.appendChild(backButton);
      content.appendChild(actions);
    }

    function renderVin(node) {
      showQuestionShell(node, 'Vehicle identification');

      var input = E('input', 'sfg-text');
      input.type = 'text';
      input.maxLength = 17;
      input.autocomplete = 'off';
      input.placeholder = '17-character VIN';
      input.style.minHeight = '0';
      input.style.height = 'auto';
      if (state.decodedVin) input.value = state.decodedVin;
      content.appendChild(input);

      var status = E('div', 'sfg-link-note', '');
      content.appendChild(status);

      var confirmWrap = E('div', '');
      content.appendChild(confirmWrap);

      var pendingDecoded = null;
      var pendingVin = '';
      var manualVinAvailable = '';

      function clearDecodedPreview() {
        pendingDecoded = null;
        pendingVin = '';
        confirmWrap.innerHTML = '';
      }

      function cleanInput() {
        var cleaned;
        if (window.SFGVehicleDecoder && typeof window.SFGVehicleDecoder.normalizeVin === 'function') {
          cleaned = window.SFGVehicleDecoder.normalizeVin(input.value);
        } else {
          cleaned = String(input.value || '').toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, '').slice(0, 17);
        }
        input.value = cleaned;
        if (pendingVin && cleaned !== pendingVin) clearDecodedPreview();
        if (manualVinAvailable && cleaned !== manualVinAvailable) manualVinAvailable = '';
      }

      input.addEventListener('input', cleanInput);
      input.addEventListener('paste', function () {
        window.setTimeout(cleanInput, 0);
      });

      var actions = E('div', 'sfg-actions');
      var decodeButton = button('Decode VIN');
      var keepVinManualButton = null;

      function setConfirmedVehicle(vin, decodedResult) {
        state.decodedVin = vin;
        state.vinDecodeTrusted = true;
        state.vehicle = decodedResult.vehicle;
        state.decoded = decodedResult.decoded;
        state.context.vehicle = decodedResult.vehicle;
        if (Flow && typeof Flow.isSupportedApplication === 'function') {
          state.context.supportedApplication = Flow.isSupportedApplication({ vehicle: decodedResult.vehicle });
        }
      }

      function showDecodedConfirmation(validation, decodedResult) {
        clearDecodedPreview();
        manualVinAvailable = '';
        pendingDecoded = decodedResult;
        pendingVin = validation.vin;
        confirmWrap.innerHTML = '';

        var box = E('div', 'sfg-result');
        box.appendChild(E('h4', '', 'Decoded vehicle'));
        box.appendChild(E('p', '', 'VIN: ' + validation.vin));

        var label = vehicleLabel(decodedResult.vehicle);
        box.appendChild(E('strong', 'sfg-analysis-title', label || 'Vehicle details could not be fully identified'));
        box.appendChild(E('p', '', 'Confirm that the decoded vehicle matches the vehicle you are troubleshooting before continuing.'));

        var confirmActions = E('div', 'sfg-actions');

        var confirmButton = button('Correct — Continue');
        confirmButton.addEventListener('click', function () {
          if (!pendingDecoded || pendingVin !== text(input.value)) {
            status.textContent = 'The VIN changed after decoding. Decode it again before continuing.';
            clearDecodedPreview();
            return;
          }

          pushHistory();
          setConfirmedVehicle(pendingVin, pendingDecoded);

          var confirmedLabel = vehicleLabel(pendingDecoded.vehicle);
          addAnswer(node.label, confirmedLabel ? (pendingVin + ' — ' + confirmedLabel) : pendingVin);
          advanceNode(node.next || 'RESULT');
        });
        confirmActions.appendChild(confirmButton);

        if (node.manualNext) {
          var incorrectButton = button('Vehicle Details Are Incorrect', 'sfg-btn sfg-btn-alt');
          incorrectButton.addEventListener('click', function () {
            if (!pendingVin) return;
            pushHistory();
            state.decodedVin = pendingVin;
            state.vinDecodeTrusted = false;
            state.vehicle = null;
            state.decoded = null;
            state.context.vehicle = null;
            delete state.context.supportedApplication;
            addFlag('VIN DECODE UNTRUSTED — customer says the automated decoded vehicle details do not match; manual vehicle identification was used for troubleshooting logic.');
            addAnswer(node.label, pendingVin + ' — decoded vehicle did not match; manual identification used');
            advanceNode(node.manualNext);
          });
          confirmActions.appendChild(incorrectButton);
        }

        var changeButton = button('Change VIN', 'sfg-btn sfg-btn-alt');
        changeButton.addEventListener('click', function () {
          clearDecodedPreview();
          status.textContent = 'Enter the correct VIN, then decode it again.';
          input.focus();
          input.select();
        });
        confirmActions.appendChild(changeButton);

        box.appendChild(confirmActions);
        confirmWrap.appendChild(box);
        status.textContent = 'VIN decoded. Review the result below before continuing.';
      }

      decodeButton.addEventListener('click', async function () {
        if (busy) return;
        cleanInput();
        clearDecodedPreview();
        manualVinAvailable = '';
        if (keepVinManualButton) keepVinManualButton.hidden = true;

        var vin = text(input.value);
        if (!vin) {
          status.textContent = 'Enter the 17-character VIN.';
          input.focus();
          return;
        }

        var validation = Codes && typeof Codes.validateVin === 'function'
          ? Codes.validateVin(vin)
          : { valid: /^[A-HJ-NPR-Z0-9]{17}$/.test(vin), vin: vin, message: 'Enter a valid 17-character VIN.' };

        if (!validation.valid) {
          status.textContent = validation.message || 'Enter a valid 17-character VIN.';
          input.focus();
          return;
        }

        if (!Codes || typeof Codes.decodeVehicleContext !== 'function') {
          manualVinAvailable = validation.vin;
          status.textContent = 'Vehicle lookup is temporarily unavailable. You can keep this validated VIN and enter the vehicle details manually, or continue without VIN and discard it.';
          if (keepVinManualButton) keepVinManualButton.hidden = false;
          return;
        }

        busy = true;
        decodeButton.disabled = true;
        decodeButton.textContent = 'Checking VIN...';
        status.textContent = 'Checking VIN...';

        try {
          var decodedResult = await Codes.decodeVehicleContext(validation.vin, { timeoutMs: 6000 });
          showDecodedConfirmation(validation, decodedResult);
        } catch (error) {
          if (error && error.sfgVinInvalid) {
            status.textContent = error.message || 'The VIN is not valid.';
          } else {
            manualVinAvailable = validation.vin;
            status.textContent = 'VIN lookup did not complete. You can keep the validated VIN and enter the vehicle details manually, retry the decode, or continue without VIN and discard it.';
            if (keepVinManualButton) keepVinManualButton.hidden = false;
          }
        } finally {
          busy = false;
          decodeButton.disabled = false;
          decodeButton.textContent = 'Decode VIN';
        }
      });
      actions.appendChild(decodeButton);

      if (node.manualNext) {
        keepVinManualButton = button('Keep VIN / Enter Vehicle Manually', 'sfg-btn sfg-btn-alt');
        keepVinManualButton.hidden = true;
        keepVinManualButton.addEventListener('click', function () {
          if (!manualVinAvailable) return;
          pushHistory();
          state.decodedVin = manualVinAvailable;
          state.vinDecodeTrusted = false;
          state.vehicle = null;
          state.decoded = null;
          state.context.vehicle = null;
          delete state.context.supportedApplication;
          addFlag('VIN LOOKUP INCOMPLETE — validated VIN retained, but manual vehicle identification was used for troubleshooting logic.');
          addAnswer(node.label, manualVinAvailable + ' — VIN retained; vehicle entered manually because decode did not complete');
          advanceNode(node.manualNext);
        });
        actions.appendChild(keepVinManualButton);
      }

      if (node.allowWithoutVin) {
        var skipButton = button('Continue Without VIN', 'sfg-btn sfg-btn-alt');
        skipButton.addEventListener('click', function () {
          pushHistory();
          input.value = '';
          clearDecodedPreview();
          manualVinAvailable = '';
          clearVinState();
          addAnswer(node.label, 'VIN not provided');
          advanceNode(node.manualNext || node.next || 'RESULT');
        });
        actions.appendChild(skipButton);
      }

      content.appendChild(actions);
      input.focus();
    }

    function renderDtc(node) {
      showQuestionShell(node, 'Diagnostic trouble codes');

      var input = E('textarea', 'sfg-text');
      input.placeholder = node.placeholder || 'Enter all current DTCs';
      content.appendChild(input);

      var error = E('div', 'sfg-error', '');
      error.hidden = true;
      content.appendChild(error);

      var actions = E('div', 'sfg-actions');
      var nextButton = button('Analyze Codes');
      nextButton.addEventListener('click', async function () {
        var value = text(input.value);
        if (!value) {
          input.focus();
          return;
        }

        var noCodes = /^(NONE|NO CODES?|N\/A)$/i.test(value);
        var analysis;

        error.hidden = true;

        if (noCodes) {
          analysis = {
            codes: [],
            results: [],
            insights: [],
            count: 0,
            source: 'engine',
            vehicle: state.vehicle || state.context.vehicle || null
          };
        } else {
          if (!Codes || typeof Codes.analyzeEngineText !== 'function') {
            error.textContent = 'The automated DTC analyzer is not available. You can still continue by entering NONE only if the vehicle has actually been scanned and no codes are present.';
            error.hidden = false;
            return;
          }

          analysis = Codes.analyzeEngineText(value, state.vehicle || state.context.vehicle || {});

          if (!analysis || !analysis.codes || !analysis.codes.length) {
            error.textContent = 'No valid diagnostic trouble codes were found. Enter codes such as P0101 or enter NONE if the vehicle has been scanned and no codes are present.';
            error.hidden = false;
            return;
          }

          /*
            SFG's local DTC analysis is completed first. Only codes without an
            SFG exact/vehicle-specific rule are eligible for the optional
            generic SAE/ISO reference lookup. The engine asset keeps those
            reference fields separate so they cannot create diagnostic checks.
          */
          var engineData = window.SFGEngineDTCData;
          if (
            engineData &&
            typeof engineData.enrichAnalysisWithGenericReference === 'function'
          ) {
            nextButton.disabled = true;
            nextButton.textContent = 'Checking Codes...';

            try {
              analysis = await engineData.enrichAnalysisWithGenericReference(
                analysis,
                { timeoutMs: 8000 }
              );
            } catch (lookupError) {
              console.warn('SFG Support UI: generic DTC reference lookup failed:', lookupError);
            } finally {
              nextButton.disabled = false;
              nextButton.textContent = 'Analyze Codes';
            }
          }
        }

        pushHistory();
        state.dtcAnalysis = analysis;
        state.context.dtcAnalysis = analysis;
        addAnswer(node.label, noCodes ? 'NONE — vehicle scanned, no current codes' : analysis.codes.join(' '));
        advanceNode(node.next || 'RESULT');
      });
      actions.appendChild(nextButton);
      content.appendChild(actions);
      input.focus();
    }

    function renderEfiError(node) {
      showQuestionShell(node, 'EFILive error');

      var input = E('textarea', 'sfg-text');
      input.placeholder = node.placeholder || 'Enter the exact EFILive $xxxx error and message';
      content.appendChild(input);

      var error = E('div', 'sfg-error', '');
      error.hidden = true;
      content.appendChild(error);

      var actions = E('div', 'sfg-actions');
      var nextButton = button('Analyze EFILive Error');
      nextButton.addEventListener('click', function () {
        var value = text(input.value);
        if (!value) {
          input.focus();
          return;
        }

        if (!Codes || typeof Codes.analyzeEFILiveText !== 'function') {
          error.textContent = 'The EFILive error-code analyzer is not available.';
          error.hidden = false;
          return;
        }

        var analysis = Codes.analyzeEFILiveText(value);
        if (!analysis || !analysis.codes || !analysis.codes.length) {
          error.textContent = 'No EFILive $xxxx error code was found. Enter the code shown by EFILive, such as $0280 or $054A, together with the exact message.';
          error.hidden = false;
          return;
        }

        pushHistory();
        state.efiAnalysis = analysis;
        state.context.efiAnalysis = analysis;
        addAnswer(node.label, value);
        advanceNode(node.next || 'RESULT');
      });
      actions.appendChild(nextButton);
      content.appendChild(actions);
      input.focus();
    }

    function startSequence(checkIds, next, sourceNode, kind) {
      var queue = unique(checkIds || []).filter(function (id) {
        return !!Flow.getCheck(id);
      });

      state.sequence = {
        queue: queue,
        next: next || 'RESULT',
        sourceNode: sourceNode || state.nodeId,
        kind: kind || 'static'
      };

      if (!queue.length) {
        var destination = state.sequence.next;
        state.sequence = null;
        advanceNode(destination);
        return;
      }

      renderCheck(queue[0]);
    }

    function renderCheck(checkId) {
      var check = Flow.getCheck(checkId);
      if (!check) {
        completeCurrentCheck([]);
        return;
      }

      applyMeta(check.meta);

      if (check.type === 'choice') {
        showQuestionShell(check);
        var grid = E('div', 'sfg-grid');
        (check.options || []).forEach(function (option) {
          var b = button(option.label, 'sfg-choice');
          b.addEventListener('click', function () {
            selectChoice(check, option, true);
          });
          grid.appendChild(b);
        });
        content.appendChild(grid);
        return;
      }

      if (check.type === 'text') {
        showQuestionShell(check);
        var input = E('textarea', 'sfg-text');
        input.placeholder = check.placeholder || '';
        content.appendChild(input);

        var actions = E('div', 'sfg-actions');
        var nextButton = button('Continue');
        nextButton.addEventListener('click', function () {
          var value = text(input.value);
          if (!value) {
            input.focus();
            return;
          }
          pushHistory();
          addAnswer(check.label, value);
          completeCurrentCheck([]);
        });
        actions.appendChild(nextButton);
        content.appendChild(actions);
        input.focus();
        return;
      }

      completeCurrentCheck([]);
    }

    function completeCurrentCheck(addChecks, removeChecks) {
      if (!state.sequence) {
        renderCurrent();
        return;
      }

      state.sequence.queue.shift();

      var removals = unique(removeChecks || []);
      if (removals.length) {
        state.sequence.queue = state.sequence.queue.filter(function (id) {
          return removals.indexOf(id) < 0;
        });
      }

      var additions = unique(addChecks || []).filter(function (id) {
        return !!Flow.getCheck(id) && state.sequence.queue.indexOf(id) < 0;
      });

      if (additions.length) {
        state.sequence.queue = additions.concat(state.sequence.queue);
      }

      if (state.sequence.queue.length) {
        renderCheck(state.sequence.queue[0]);
        return;
      }

      var next = state.sequence.next || 'RESULT';
      state.sequence = null;
      advanceNode(next);
    }

    function startDynamicVehicleChecks(node) {
      var analysis = state.dtcAnalysis || state.context.dtcAnalysis || {
        codes: [], results: [], insights: [], count: 0
      };

      var plan = Flow.deriveDtcPlan(analysis, state.context) || { checks: [], notes: [] };

      if (plan.status) state.status = plan.status;
      if (plan.follow) state.follow = plan.follow;
      addEvidence(plan.evidence || []);
      (plan.flags || []).forEach(addFlag);
      (plan.notes || []).forEach(addResultNote);

      var checks = cloneArray(plan.checks || []);

      /*
        Use the short concern-specific fallback only when there are no DTCs or
        the submitted DTCs are not specifically recognized. A recognized code
        that only calls for a datalog/result does not need unrelated generic
        questions merely because its check list is empty.
      */
      var recognizedDtc = !!(analysis.results || []).some(function (result) {
        return result && result.matched === true;
      });
      if (!checks.length && !(plan.notes || []).length && (!analysis.codes.length || !recognizedDtc)) {
        checks = Flow.fallbackVehicleChecks(state.context) || [];
      }

      applyMeta(node.meta);
      startSequence(checks, node.next || 'RESULT', node.id, 'dynamic_vehicle');
    }

    function startDynamicEfiChecks(node) {
      var analysis = state.efiAnalysis || state.context.efiAnalysis || {
        codes: [], results: [], count: 0
      };
      var plan = Flow.deriveEfiPlan(analysis, state.context) || { checks: [], notes: [] };

      if (plan.status) state.status = plan.status;
      addEvidence(plan.evidence || []);
      (plan.flags || []).forEach(addFlag);
      (plan.notes || []).forEach(addResultNote);
      if (plan.resultLink) state.resultLink = plan.resultLink;
      if (plan.resultLinkLabel) state.resultLinkLabel = plan.resultLinkLabel;

      applyMeta(node.meta);
      startSequence(plan.checks || [], node.next || 'RESULT', node.id, 'dynamic_efi');
    }

    function goBack() {
      pending = null;

      if (!navHistory.length) {
        categories();
        return;
      }

      var previous = navHistory.pop();
      restore(previous);
      renderCurrent();
    }

    function vehicleLabel(vehicle) {
      vehicle = vehicle || state.vehicle;
      if (!vehicle) return '';

      if (vehicle.title) {
        var suffix = [];
        if (vehicle.engine) suffix.push(vehicle.engine);
        if (vehicle.targetLabel && suffix.indexOf(vehicle.targetLabel) < 0) suffix.push(vehicle.targetLabel);
        return vehicle.title + (suffix.length ? ' — ' + suffix.join(' · ') : '');
      }

      var title = [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ');
      if (vehicle.engine) title += (title ? ' — ' : '') + vehicle.engine;
      return text(title);
    }

    function renderAnalysisSection(analysis, title) {
      if (!analysis || !analysis.results || !analysis.results.length) return;

      var wrap = E('div', 'sfg-result');
      wrap.appendChild(E('h4', '', title));

      analysis.results.forEach(function (result) {
        var code = text(result.code);
        var displayTitle = text(result.title);
        var customerText = result.customerText || '';

        if (analysis.source === 'engine' && Flow && typeof Flow.customerDtcTitle === 'function') {
          displayTitle = text(Flow.customerDtcTitle(result, state.context) || displayTitle);
        }

        if (analysis.source === 'engine' && Flow && typeof Flow.customerDtcText === 'function') {
          customerText = Flow.customerDtcText(result, state.context) || '';
        }

        /*
          A generic reference is display-only. It supplies a proper generic
          code name/definition when SFG has no exact rule, but it never replaces
          an SFG exact or vehicle-specific diagnostic rule.
        */
        if (
          analysis.source === 'engine' &&
          result.genericReference &&
          (result.matchType === 'family' || result.matchType === 'none')
        ) {
          displayTitle = text(result.genericReferenceTitle || displayTitle);

          var referenceHeading = [code, displayTitle].filter(Boolean).join(' — ');
          if (referenceHeading) {
            wrap.appendChild(E('strong', 'sfg-analysis-title', referenceHeading));
          }

          var referenceDescription = text(result.genericReferenceDescription);
          if (referenceDescription) {
            wrap.appendChild(E('p', '', referenceDescription));
          }

          wrap.appendChild(E(
            'p',
            'sfg-link-note',
            'Generic SAE/ISO reference only — SFG does not currently have a more-specific automated diagnostic rule for this code. The definition identifies what the code reports; it does not by itself identify the failed part or prove a tune issue.'
          ));
          return;
        }

        var heading = [code, displayTitle].filter(Boolean).join(' — ');
        if (heading) wrap.appendChild(E('strong', 'sfg-analysis-title', heading));
        if (customerText) wrap.appendChild(E('p', '', customerText));
      });

      if (
        analysis.source === 'engine' &&
        analysis.genericReferenceLookupAttempted &&
        analysis.genericReferenceLookupAvailable === false
      ) {
        wrap.appendChild(E(
          'p',
          'sfg-link-note',
          'The generic SAE/ISO code reference was temporarily unavailable. SFG retained the exact code and did not guess at a definition.'
        ));
      }

      if (analysis.insights && analysis.insights.length) {
        var insightTitle = E('strong', 'sfg-analysis-title', 'Combined-code findings');
        wrap.appendChild(insightTitle);
        analysis.insights.forEach(function (insight) {
          var line = text(insight.text || insight.title);
          if (line) wrap.appendChild(E('p', '', line));
        });
      }

      content.appendChild(wrap);
    }

    function renderResultHelp() {
      var box;

      if (state.follow === Flow.follow.HP_LOG) {
        box = E('div', 'sfg-result');
        box.appendChild(E('h4', '', 'HP Tuners Info Log now; driving datalog to follow'));
        box.appendChild(E('p', '', 'Create an HP Tuners Info Log and include it with the ticket. The Info Log shows calibration information and fault/status information; it is not a driving datalog. A driving datalog is also required for this concern, but do not build your own channel list. SFG will provide the correct datalog channels after reviewing the ticket and Info Log.'));
        if (Flow.urls.hpInstructions) {
          var hpActions = E('div', 'sfg-actions');
          hpActions.appendChild(linkButton('Open MPVI Instructions', Flow.urls.hpInstructions, 'sfg-btn sfg-btn-alt'));
          box.appendChild(hpActions);
        }
        content.appendChild(box);
      } else if (state.follow === Flow.follow.HP_INFO) {
        box = E('div', 'sfg-result');
        box.appendChild(E('h4', '', 'HP Tuners Info Log requested'));
        box.appendChild(E('p', '', 'Create an Info Log and send it with the ticket. The Info Log shows calibration information and fault/status information; it is separate from a driving datalog. The MPVI instructions show where to find it.'));
        if (Flow.urls.hpInstructions) {
          var infoActions = E('div', 'sfg-actions');
          infoActions.appendChild(linkButton('Open MPVI Instructions', Flow.urls.hpInstructions, 'sfg-btn sfg-btn-alt'));
          box.appendChild(infoActions);
        }
        content.appendChild(box);
      } else if (state.follow === Flow.follow.EFI_LOG) {
        box = E('div', 'sfg-result');
        box.appendChild(E('h4', '', 'EFILive datalog requested'));
        box.appendChild(E('p', '', 'A datalog that captures the concern is needed for SFG review. Use the SFG EFILive instructions to create the log and send it with the ticket.'));
        if (Flow.urls.efiInstructions) {
          var efiActions = E('div', 'sfg-actions');
          efiActions.appendChild(linkButton('Open EFILive Instructions', Flow.urls.efiInstructions, 'sfg-btn sfg-btn-alt'));
          box.appendChild(efiActions);
        }
        content.appendChild(box);
      } else if (state.follow === Flow.follow.EZ_REQUEST) {
        box = E('div', 'sfg-result');
        box.appendChild(E('h4', '', 'EZ Lynk logs are by request'));
        box.appendChild(E('p', '', 'Create the ticket first. SFG will request an EZ Lynk datalog if one is needed for the concern.'));
        content.appendChild(box);
      } else if (state.follow === Flow.follow.SWITCH_LOG) {
        box = E('div', 'sfg-result');
        box.appendChild(E('h4', '', 'Do not guess at logging channels'));
        box.appendChild(E('p', '', 'Create the ticket first. SFG will provide the correct logging channels if power-level verification is needed.'));
        content.appendChild(box);
      }

      if (state.resultLink) {
        var actions = E('div', 'sfg-actions');
        actions.appendChild(linkButton(state.resultLinkLabel || 'Open Link', state.resultLink, 'sfg-btn sfg-btn-alt'));
        content.appendChild(actions);
      }
    }

    function troubleshootingSummary() {
      var flow = currentFlow();
      var lines = [
        'Support Type: ' + (flow ? flow.title : state.flowKey),
        'Status: ' + state.status
      ];

      if (state.vehicle) {
        lines.push('Vehicle: ' + vehicleLabel(state.vehicle));
      } else if (state.decodedVin) {
        lines.push('VIN: ' + state.decodedVin);
      }

      if (state.unresolved.length) {
        lines.push('', 'Unresolved basic checks:');
        state.unresolved.forEach(function (item) {
          lines.push('- ' + item);
        });
      }

      state.answers.forEach(function (item) {
        lines.push('', item.question, 'Answer: ' + item.answer);
      });

      return lines.join('\n');
    }

    function resultsScreen() {
      view = 'results';
      pending = null;
      hideTicket();
      nav.hidden = true;
      progress.hidden = false;
      progressBar.style.width = '100%';
      progressText.textContent = 'Relevant checks complete';
      content.innerHTML = '';
      finalizeStatus();

      content.appendChild(E('h3', 'sfg-q', statusHeadline()));
      content.appendChild(E('p', 'sfg-desc', statusDescription()));

      if (state.unresolved.length) {
        var unresolvedBox = E('div', 'sfg-warning');
        unresolvedBox.appendChild(E('h4', '', 'Unresolved basic checks'));
        var ul = document.createElement('ul');
        state.unresolved.forEach(function (item) {
          var li = document.createElement('li');
          li.textContent = item;
          ul.appendChild(li);
        });
        unresolvedBox.appendChild(ul);
        content.appendChild(unresolvedBox);
      }

      state.resultNotes.forEach(function (note) {
        var box = E('div', 'sfg-result');
        box.appendChild(E('h4', '', state.status === Flow.status.VEHICLE ? 'Current direction' : (Flow.status.CONFIGURATION && state.status === Flow.status.CONFIGURATION ? 'Calibration configuration check' : 'SFG Support')));
        box.appendChild(E('p', '', note));
        content.appendChild(box);
      });

      renderAnalysisSection(state.dtcAnalysis, 'Diagnostic trouble codes');
      renderAnalysisSection(state.efiAnalysis, 'EFILive error analysis');
      renderResultHelp();

      var summary = E('textarea', 'sfg-summary');
      summary.readOnly = true;
      summary.value = troubleshootingSummary();
      content.appendChild(summary);

      var actions = E('div', 'sfg-actions');
      var ticketButton = button(
        Flow.status.RETRY_FLASH && state.status === Flow.status.RETRY_FLASH
          ? 'Create Ticket If Retry Fails'
          : 'Create Support Ticket'
      );
      var copyButton = button('Copy Summary', 'sfg-btn sfg-btn-alt');
      var restartButton = button('Start Over', 'sfg-btn sfg-btn-alt');

      ticketButton.addEventListener('click', ticketScreen);
      copyButton.addEventListener('click', function () {
        copyToClipboard(troubleshootingSummary(), copyButton);
      });
      restartButton.addEventListener('click', categories);

      actions.appendChild(ticketButton);
      actions.appendChild(copyButton);
      actions.appendChild(restartButton);
      content.appendChild(actions);
    }

    function resetTicketForm() {
      if (!ticketForm) return;
      ticketForm.reset();
      ticketForm.dataset.sfgSubmitting = '';
      ticketForm.dataset.sfgReady = '';

      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = 'Submit Support Ticket';
      }

      var ticketVin = root.querySelector('[data-c="vin"]');
      if (ticketVin) {
        ticketVin.readOnly = false;
        ticketVin.removeAttribute('aria-readonly');
      }

      if (validationBox) validationBox.hidden = true;
    }

    function ticketScreen() {
      finalizeStatus();
      view = 'ticket';
      pending = null;
      state.ticketId = null;
      nav.hidden = true;
      progress.hidden = false;
      progressBar.style.width = '100%';
      progressText.textContent = 'Create support ticket';
      content.innerHTML = '';
      content.appendChild(E('h3', 'sfg-q', 'Create Support Ticket'));

      var flow = currentFlow();
      var generic = state.flowKey === 'general';

      content.appendChild(E(
        'p',
        'sfg-desc',
        generic
          ? 'Send a written message directly to SFG Support.'
          : 'Your troubleshooting information will be included automatically. Add your contact information and message below.'
      ));

      var questionLabel = root.querySelector('[data-question-label]');
      var question = root.querySelector('[data-c="question"]');
      var idNote = root.querySelector('[data-id-note]');
      var vinInput = root.querySelector('[data-c="vin"]');

      if (vinInput) {
        if (state.decodedVin) {
          vinInput.value = state.decodedVin;
          vinInput.readOnly = true;
          vinInput.setAttribute('aria-readonly', 'true');
        } else {
          vinInput.readOnly = false;
          vinInput.removeAttribute('aria-readonly');
        }
      }

      if (questionLabel) {
        questionLabel.textContent = generic
          ? 'Message for SFG Support *'
          : state.flowKey === 'tune'
            ? 'Describe Your Question or Issue *'
            : 'Describe the Concern *';
      }

      if (question) {
        question.placeholder = generic
          ? 'Tell us what you need help with. Include any details you think may be relevant.'
          : state.flowKey === 'tune'
            ? 'Describe what you would like to know, or the tune-file issue you are having.'
            : 'Describe the concern in your own words, including anything not covered by the guided checks.';
      }

      if (idNote) {
        var needsLookup = !!(flow && !flow.allowTicketWithoutVinOrOrder);
        idNote.hidden = !needsLookup;
        if (needsLookup) {
          idNote.textContent = state.decodedVin
            ? 'VIN captured during guided checks and locked to this troubleshooting result. Add the order number if available.'
            : 'For this technical support ticket, enter either the VIN or order # so SFG can locate the correct vehicle, order, or tune. Both are helpful when available.';
        }
      }

      ticket.hidden = false;
    }

    function torontoDate() {
      try {
        var parts = new Intl.DateTimeFormat('en-CA', {
          timeZone: 'America/Toronto',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        }).formatToParts(new Date());
        var values = {};
        parts.forEach(function (part) {
          values[part.type] = part.value;
        });
        return values.year + values.month + values.day;
      } catch (error) {
        var d = new Date();
        return d.getFullYear() + String(d.getMonth() + 1).padStart(2, '0') + String(d.getDate()).padStart(2, '0');
      }
    }

    function makeTicketId() {
      var alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
      var result = '';
      var values = new Uint32Array(7);

      if (window.crypto && window.crypto.getRandomValues) {
        window.crypto.getRandomValues(values);
        for (var i = 0; i < values.length; i++) result += alphabet[values[i] % alphabet.length];
      } else {
        for (var j = 0; j < 7; j++) result += alphabet[Math.floor(Math.random() * alphabet.length)];
      }

      return 'SFG-' + torontoDate() + '-' + result;
    }

    function analysisForStaff(analysis) {
      if (!analysis) return '';

      var base = '';

      if (Codes && typeof Codes.formatStaffAnalysis === 'function') {
        try {
          base = Codes.formatStaffAnalysis(analysis) || '';
        } catch (error) {
          console.warn('SFG Support UI: staff analysis formatting failed:', error);
        }
      }

      if (!base) {
        var lines = [];
        (analysis.results || []).forEach(function (result) {
          lines.push([result.code, result.title].filter(Boolean).join(' — '));
          if (result.customerText) lines.push(result.customerText);
        });
        base = lines.join('\n');
      }

      /*
        Keep the existing staff formatter untouched, then append any external
        generic reference separately so staff can distinguish a code definition
        from SFG diagnostic logic.
      */
      var referenceLines = [];
      (analysis.results || []).forEach(function (result) {
        if (!result || !result.genericReference) return;

        referenceLines.push(
          [
            result.code,
            result.genericReferenceTitle
          ].filter(Boolean).join(' — ')
        );

        if (result.genericReferenceDescription) {
          referenceLines.push('Reference definition: ' + result.genericReferenceDescription);
        }

        referenceLines.push('Reference only — no SFG-specific diagnostic rule was assigned from this lookup.');
      });

      if (referenceLines.length) {
        return [
          base,
          'GENERIC SAE/ISO REFERENCE',
          referenceLines.join('\n')
        ].filter(Boolean).join('\n\n');
      }

      return base;
    }

    function buildTicketBody(customer) {
      var flow = currentFlow();
      finalizeStatus();
      var lines = [
        SUPPORT_MARKER,
        '',
        'TICKET: ' + state.ticketId,
        'STATUS: ' + state.status,
        'TYPE: ' + (flow ? flow.title : state.flowKey)
      ];

      if (state.vehicle) {
        lines.push('', 'DECODED VEHICLE', vehicleLabel(state.vehicle));
        if (state.vehicle.targetKey) lines.push('Platform target: ' + state.vehicle.targetKey);
        if (state.vehicle.transmission) lines.push('Transmission: ' + state.vehicle.transmission);
      } else if (state.decodedVin && state.vinDecodeTrusted === false) {
        lines.push('', 'VIN DECODE STATUS', 'VIN retained, but automated vehicle decode was not trusted/available. Manual vehicle identification was used for troubleshooting logic.');
      }

      if (state.flags.length) {
        lines.push('', 'INTERNAL FLAGS');
        state.flags.forEach(function (flag) { lines.push('- ' + flag); });
      }

      if (state.evidence.length) {
        lines.push('', 'DIAGNOSTIC EVIDENCE');
        state.evidence.forEach(function (item) {
          lines.push('- ' + String(item.type || '').toUpperCase() + ' [' + Number(item.weight || 0) + ']: ' + (item.reason || 'No reason recorded'));
        });
      }

      if (state.unresolved.length) {
        lines.push('', 'UNRESOLVED BASIC CHECKS');
        state.unresolved.forEach(function (item) {
          lines.push('- ' + item);
        });
      }

      lines.push(
        '',
        'CUSTOMER',
        'Name: ' + customer.name,
        'Email: ' + customer.email,
        'Phone: ' + (customer.phone || 'Not provided'),
        'VIN: ' + (customer.vin || 'Not provided'),
        'Order: ' + (customer.order || 'Not provided'),
        '',
        'MESSAGE FOR SFG SUPPORT',
        customer.question
      );

      if (state.resultNotes.length) {
        lines.push('', 'WORKFLOW NOTES');
        state.resultNotes.forEach(function (note) {
          lines.push('- ' + note);
        });
      }

      if (state.dtcAnalysis) {
        var dtcText = analysisForStaff(state.dtcAnalysis);
        if (dtcText) lines.push('', 'AUTOMATED DTC ANALYSIS', dtcText);
      }

      if (state.efiAnalysis) {
        var efiText = analysisForStaff(state.efiAnalysis);
        if (efiText) lines.push('', 'AUTOMATED EFILIVE ANALYSIS', efiText);
      }

      if (state.answers.length) {
        lines.push('', 'TROUBLESHOOTING');
        state.answers.forEach(function (item) {
          lines.push('', item.question, 'Answer: ' + item.answer);
        });
      }

      lines.push('', '', 'ADDITIONAL NOTES', customer.notes || 'No additional notes.');
      return lines.join('\n');
    }

    function cleanTicketVin(input) {
      if (!input) return;
      var position = input.selectionStart || 0;
      var cleaned;

      if (window.SFGVehicleDecoder && typeof window.SFGVehicleDecoder.normalizeVin === 'function') {
        cleaned = window.SFGVehicleDecoder.normalizeVin(input.value);
      } else {
        cleaned = String(input.value || '').toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, '').slice(0, 17);
      }

      input.value = cleaned;
      try {
        input.setSelectionRange(Math.min(position, cleaned.length), Math.min(position, cleaned.length));
      } catch (error) {}
    }

    function validateTicket() {
      var name = root.querySelector('[data-c="name"]');
      var email = root.querySelector('[data-c="email"]');
      var order = root.querySelector('[data-c="order"]');
      var vin = root.querySelector('[data-c="vin"]');
      var question = root.querySelector('[data-c="question"]');
      var flow = currentFlow();

      function fail(message, element) {
        if (validationBox) {
          validationBox.textContent = message;
          validationBox.hidden = false;
        }
        if (element) element.focus();
        return false;
      }

      if (!name || !text(name.value)) return fail('Please enter your name.', name);
      if (!email || !text(email.value) || !email.checkValidity()) return fail('Please enter a valid email address.', email);

      cleanTicketVin(vin);
      if (vin && vin.value) {
        var validation = Codes && typeof Codes.validateVin === 'function'
          ? Codes.validateVin(vin.value)
          : { valid: /^[A-HJ-NPR-Z0-9]{17}$/.test(vin.value) };
        if (!validation.valid) return fail(validation.message || 'If a VIN is entered, it must be a valid 17-character VIN.', vin);
      }

      if (state.decodedVin && vin && text(vin.value) !== state.decodedVin) {
        return fail('This troubleshooting result is tied to the VIN entered during the guided checks. Start over to diagnose a different vehicle.', vin);
      }

      if (flow && !flow.allowTicketWithoutVinOrOrder && !text(vin && vin.value) && !text(order && order.value)) {
        return fail('Please enter either the VIN or order # so SFG can locate the correct vehicle, order, or tune.', order);
      }

      if (!question || !text(question.value)) {
        if (state.flowKey === 'general') return fail('Please enter your message for SFG Support.', question);
        if (state.flowKey === 'tune') return fail('Please describe your tune-file question or issue.', question);
        return fail('Please describe the concern.', question);
      }

      if (validationBox) validationBox.hidden = true;
      return true;
    }

    function submitTicket() {
      if (ticketForm.dataset.sfgReady === '1' || ticketForm.dataset.sfgSubmitting === '1') return;
      if (!validateTicket()) return;

      ticketForm.dataset.sfgSubmitting = '1';
      state.ticketId = makeTicketId();

      var customer = {
        name: text(root.querySelector('[data-c="name"]').value),
        email: text(root.querySelector('[data-c="email"]').value),
        phone: text(root.querySelector('[data-c="phone"]').value),
        order: text(root.querySelector('[data-c="order"]').value),
        vin: text(root.querySelector('[data-c="vin"]').value),
        question: text(root.querySelector('[data-c="question"]').value),
        notes: text(root.querySelector('[data-c="notes"]').value)
      };

      var idField = root.querySelector('[data-f="id"]');
      var typeField = root.querySelector('[data-f="type"]');
      var bodyField = root.querySelector('[data-f="body"]');

      if (idField) idField.value = state.ticketId;
      if (typeField) typeField.value = currentFlow() ? currentFlow().title : state.flowKey;
      if (bodyField) bodyField.value = buildTicketBody(customer);

      var returnTo = ticketForm.querySelector('input[name="return_to"]');
      if (returnTo) {
        returnTo.value = location.pathname +
          '?sfg_submitted=1' +
          '&sfg_ticket=' + encodeURIComponent(state.ticketId) +
          '&sfg_follow=' + encodeURIComponent(state.follow || '') +
          '#' + encodeURIComponent(rootAnchor);
      }

      if (submitButton) {
        submitButton.disabled = true;
        submitButton.textContent = 'Submitting...';
      }

      /*
        Important: requestSubmit() is intentional. It dispatches a real native
        form submit event so Shopify's contact-form/CAPTCHA machinery can run.
      */
      ticketForm.dataset.sfgReady = '1';
      ticketForm.dataset.sfgSubmitting = '';

      if (ticketForm.requestSubmit) {
        ticketForm.requestSubmit();
      } else {
        var nativeSubmit = document.createElement('button');
        nativeSubmit.type = 'submit';
        nativeSubmit.hidden = true;
        ticketForm.appendChild(nativeSubmit);
        nativeSubmit.click();
        nativeSubmit.remove();
      }
    }

    function successScreen(id, follow) {
      view = 'success';
      nav.hidden = true;
      progress.hidden = true;
      ticket.hidden = true;
      content.innerHTML = '';

      content.appendChild(E('h3', 'sfg-q', 'Support ticket submitted to SFG Support'));
      content.appendChild(E('p', 'sfg-desc', 'Your request has been sent to SFG Support. Please use this ticket ID for any follow-up.'));
      content.appendChild(E('div', 'sfg-success-ticket', id));

      var box;
      if (follow === Flow.follow.HP_LOG || follow === Flow.follow.HP_INFO) {
        box = E('div', 'sfg-result');
        box.appendChild(E('h4', '', 'Email the requested HP Tuners Info Log'));
        if (follow === Flow.follow.HP_LOG) {
          box.appendChild(E('p', '', 'Email the Info Log to ' + SUPPORT_EMAIL + ' and use only the ticket ID above as the subject. The Info Log is not the driving datalog. SFG will review the ticket and Info Log, then provide the correct channel list for the required driving datalog.'));
        } else {
          box.appendChild(E('p', '', 'Email the Info Log to ' + SUPPORT_EMAIL + ' and use only the ticket ID above as the subject.'));
        }
        content.appendChild(box);
      } else if (follow === Flow.follow.EFI_LOG) {
        box = E('div', 'sfg-result');
        box.appendChild(E('h4', '', 'Email the requested EFILive datalog'));
        box.appendChild(E('p', '', 'Email the datalog that captures the concern to ' + SUPPORT_EMAIL + ' and use only the ticket ID above as the subject.'));
        content.appendChild(box);
      } else if (follow === Flow.follow.EZ_REQUEST) {
        box = E('div', 'sfg-result');
        box.appendChild(E('h4', '', 'No EZ Lynk log is required yet'));
        box.appendChild(E('p', '', 'SFG will request a datalog if one is needed after reviewing the ticket.'));
        content.appendChild(box);
      } else if (follow === Flow.follow.SWITCH_LOG) {
        box = E('div', 'sfg-result');
        box.appendChild(E('h4', '', 'Wait for SFG logging instructions'));
        box.appendChild(E('p', '', 'SFG will provide the correct logging channels if power-level verification is needed.'));
        content.appendChild(box);
      }

      var actions = E('div', 'sfg-actions');
      var copyButton = button('Copy Ticket ID');
      var anotherButton = button('Start Another Request', 'sfg-btn sfg-btn-alt');
      copyButton.addEventListener('click', function () {
        copyToClipboard(id, copyButton);
      });
      anotherButton.addEventListener('click', categories);
      actions.appendChild(copyButton);
      actions.appendChild(anotherButton);
      content.appendChild(actions);
    }

    function copyToClipboard(value, sourceButton) {
      function copied() {
        if (!sourceButton) return;
        var old = sourceButton.textContent;
        sourceButton.textContent = 'Copied';
        window.setTimeout(function () {
          sourceButton.textContent = old;
        }, 1800);
      }

      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(value).then(copied).catch(function () {});
        return;
      }

      var temporary = E('textarea');
      temporary.value = value;
      temporary.style.position = 'fixed';
      temporary.style.opacity = '0';
      document.body.appendChild(temporary);
      temporary.select();
      try {
        document.execCommand('copy');
        copied();
      } catch (error) {}
      temporary.remove();
    }

    function submittedState() {
      var params = new URLSearchParams(location.search);
      if (params.get('sfg_submitted') !== '1') return null;
      return {
        id: params.get('sfg_ticket') || '',
        follow: params.get('sfg_follow') || ''
      };
    }

    function cleanUrl() {
      var url = new URL(location.href);
      url.searchParams.delete('sfg_submitted');
      url.searchParams.delete('sfg_ticket');
      url.searchParams.delete('sfg_follow');
      window.history.replaceState({}, '', url.pathname + (url.searchParams.toString() ? '?' + url.searchParams.toString() : '') + url.hash);
    }

    back.addEventListener('click', goBack);

    if (ticketBackButton) {
      ticketBackButton.addEventListener('click', function () {
        var flow = currentFlow();
        if (flow && flow.direct) categories();
        else resultsScreen();
      });
    }

    ticketForm.addEventListener('submit', function (event) {
      if (ticketForm.dataset.sfgReady === '1') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      submitTicket();
    }, true);

    if (submitButton) submitButton.addEventListener('click', submitTicket);

    ticketForm.addEventListener('keydown', function (event) {
      if (event.key === 'Enter' && event.target && event.target.tagName !== 'TEXTAREA') {
        event.preventDefault();
        submitTicket();
      }
    });

    var ticketVin = root.querySelector('[data-c="vin"]');
    if (ticketVin) {
      ticketVin.addEventListener('input', function () {
        cleanTicketVin(ticketVin);
      });
      ticketVin.addEventListener('paste', function () {
        window.setTimeout(function () {
          cleanTicketVin(ticketVin);
        }, 0);
      });
    }

    var submitted = submittedState();
    if (submitted && submitted.id) {
      successScreen(submitted.id, submitted.follow);
      cleanUrl();
    } else {
      categories();
    }

    window.addEventListener('pageshow', function (event) {
      var navigation = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
      if (event.persisted || (navigation && navigation.type === 'back_forward')) {
        cleanUrl();
        categories();
      }
    });
  }

  function initAllSupport(scope) {
    scope = scope || document;
    scope.querySelectorAll('[data-sfg-support]').forEach(initSupport);
  }

  window.SFGSupportUI = {
    version: '2.8.0',
    initAll: initAllSupport
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAllSupport(document);
    });
  } else {
    initAllSupport(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAllSupport(event.target);
  });
})();
